/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useRef, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  NativeSyntheticEvent,
  NativeScrollEvent,
  FlatListProps,
  ViewToken,
  Pressable,
  Animated,
  LayoutChangeEvent,
  TouchableOpacity,
  type ImageSourcePropType,
  type TextStyle,
} from 'react-native';
import {
  breakpoints,
  pd,
  useContainerWidth,
  useDeviceInfo,
} from '@airasia-rn/ui';
import {
  getColor,
  getShape,
  getTextStyle,
  Orientation,
} from '@airasia-phoenix/react-native/utils/utils';
import ContentDetails, {
  ContentCardOrientation,
  type ContentDetailsProps,
} from '@airasia-phoenix/react-native/widgets/HorizontalContentCardView/ContentDetailsView';
import ContentCardGroupView from '@airasia-phoenix/react-native/widgets/ContentCardGroupView';
import { type ContentCardViewProps } from '@airasia-phoenix/react-native/widgets/ContentCardView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import { styles } from './ArticleCarousel.styles';
import {
  type ArticleCarouselData,
  type ArticleSection,
} from '../../models/homepageBlockTypes';
import { useLogger } from '@airasia-rn/core-logger';
import {
  HomePageEvents,
  trackArticleCarouselClicked,
  trackArticleCarouselViewed,
} from '../../analytics/homepage';
import { type BackgroundImageSet } from '../../models/homepageBlockTypes';
import { parseDensitySrcSet } from '../../utils/responsiveImage';
import {
  DESKTOP_CARD_SCALE,
  animateDesktopArticleCardScale,
} from './ArticleCarouselView.web.animation';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const DESKTOP_DEFAULT_CARD_WIDTH = 288;
const DESKTOP_DEFAULT_CARD_HEIGHT = 177;
const DESKTOP_FIT_MAX_CARDS = 4;
const DESKTOP_CHEVRON_SIZE = 32;
/** RN: 0 = no line cap (show full title / subtitle). */
const DESKTOP_UNLIMITED_TEXT_LINES = 0;

const getWebViewportWidth = (fallbackWidth: number) => {
  if (typeof window === 'undefined') {
    return fallbackWidth;
  }

  return window.innerWidth || fallbackWidth;
};

const getArticleViewportImageSrc = ({
  backgroundImage,
  fallbackSrc,
  fallbackWidth,
}: {
  backgroundImage?: BackgroundImageSet;
  fallbackSrc?: string;
  fallbackWidth: number;
}) => {
  const width = getWebViewportWidth(fallbackWidth);
  const images = parseDensitySrcSet(backgroundImage?.srcSet);
  const defaultSrc = backgroundImage?.src || fallbackSrc;

  if (width < breakpoints.md) {
    return images.mobile || defaultSrc;
  }

  if (width < breakpoints.lg) {
    return images.tablet || images.mobile || defaultSrc;
  }

  return images.desktop || images.tablet || images.mobile || defaultSrc;
};

/**
 * Desktop article cards render ContentDetails directly. ContentCardView always
 * passes secondaryTextLines={4} and defaults primary to 2 lines, which truncates
 * long copy when cards are narrowed to fit four per row.
 */
function DesktopArticleCardContent({
  card,
  cardWidth,
}: {
  card: ContentCardViewProps;
  cardWidth: number;
}) {
  const details = card.contentDetailsPropsValues;
  const imageWidth = cardWidth;
  const imageHeight = card.imageHeight;

  const contentDetailsProps: ContentDetailsProps = {
    orientation: ContentCardOrientation.VERTICAL,
    width: imageWidth,
    imageWidth,
    primaryText: details?.primaryText,
    secondaryText: details?.secondaryText,
    primaryTextLines: DESKTOP_UNLIMITED_TEXT_LINES,
    secondaryTextLines: DESKTOP_UNLIMITED_TEXT_LINES,
  };

  const imageIconProps =
    typeof card.image === 'object' &&
    card.image !== null &&
    !Array.isArray(card.image) &&
    'url' in card.image
      ? { url: card.image.url }
      : { source: card.image as ImageSourcePropType };

  return (
    <TouchableOpacity
      accessibilityRole="button"
      onPress={card.onPress}
      disabled={!card.onPress}
      activeOpacity={0.2}
      style={[styles.desktopArticleCardPressable, { width: imageWidth }]}
    >
      <View style={{ width: imageWidth, height: imageHeight }}>
        <IconView
          {...imageIconProps}
          width={imageWidth}
          height={imageHeight}
          style={getShape(pd.comp.content_card.image.shape)}
        />
      </View>
      {(details?.primaryText || details?.secondaryText) && (
        <ContentDetails {...contentDetailsProps} />
      )}
    </TouchableOpacity>
  );
}

function HoverableArticleCard({
  card,
  index,
  total,
  cardWidth,
}: {
  card: ContentCardViewProps;
  index: number;
  total: number;
  cardWidth: number;
}) {
  const scale = useRef(new Animated.Value(1)).current;

  return (
    <AnimatedPressable
      onHoverIn={() =>
        animateDesktopArticleCardScale({
          scale,
          toValue: DESKTOP_CARD_SCALE,
        })
      }
      onHoverOut={() =>
        animateDesktopArticleCardScale({
          scale,
          toValue: 1,
        })
      }
      style={[
        styles.desktopCardContainer,
        index > 0 && styles.desktopCardSpacingLeft,
        index < total - 1 && styles.desktopCardSpacingRight,
        { width: cardWidth, transform: [{ scale }] },
      ]}
    >
      <DesktopArticleCardContent card={card} cardWidth={cardWidth} />
    </AnimatedPressable>
  );
}

function ArticleCarouselSection({
  section,
  isMobileWidth,
  isLast,
  desktopFitCardDivisor,
}: {
  section: ArticleSection;
  isMobileWidth: boolean;
  isLast: boolean;
  desktopFitCardDivisor: number;
}) {
  const listRef = useRef<FlatList>(null);
  const { logEvent } = useLogger();
  const viewedIndexesRef = useRef<Set<number>>(new Set());
  const trackArticleViewRef = useRef<(index: number) => void>(() => {});
  const onViewableItemsChanged = useRef(
    ({
      viewableItems,
    }: {
      viewableItems: ViewToken<any>[];
      changed: ViewToken<any>[];
    }) => {
      viewableItems.forEach((item) => {
        if (item.isViewable && typeof item.index === 'number') {
          trackArticleViewRef.current(item.index);
        }
      });
    },
  ).current;
  const {
    containerWidth: desktopContainerWidth,
    onLayout: onDesktopInsetLayout,
  } = useContainerWidth();
  const [desktopContentWidth, setDesktopContentWidth] = useState(0);
  const [desktopScrollOffset, setDesktopScrollOffset] = useState(0);

  if (!section.articles.length) {
    return null;
  }

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    setDesktopScrollOffset(event.nativeEvent.contentOffset.x);
  };

  const trackArticleView = (index: number) => {
    const article = section.articles[index];
    if (!article || viewedIndexesRef.current.has(index)) return;
    viewedIndexesRef.current.add(index);
    logEvent(
      HomePageEvents.view,
      trackArticleCarouselViewed({
        sectionTitle: section.title,
        label: `${article.title}|${index + 1}`,
      }),
    );
  };

  trackArticleViewRef.current = trackArticleView;
  const fallbackViewportWidth = isMobileWidth ? breakpoints.xs : breakpoints.lg;

  const handleArticlePress = (
    index: number,
    article: ArticleSection['articles'][number],
  ) => {
    const hasArticle = section.articles[index];
    if (!hasArticle) return;
    if (article.redirectionUrl && typeof window !== 'undefined') {
      window.open(article.redirectionUrl, '_blank', 'noopener,noreferrer');
    }
    logEvent(
      HomePageEvents.tap,
      trackArticleCarouselClicked({
        sectionTitle: section.title,
        label: `${article.title}|${index + 1}`,
      }),
    );
  };

  const isDesktopMeasured = !isMobileWidth && desktopContainerWidth > 0;

  const desktopCardGap = pd.ref.space.spacing20.px * 2;
  const visibleGapTotal = (desktopFitCardDivisor - 1) * desktopCardGap;
  const fittedDesktopCardWidth = isDesktopMeasured
    ? Math.max(
        0,
        Math.min(
          DESKTOP_DEFAULT_CARD_WIDTH,
          Math.floor(
            (desktopContainerWidth - visibleGapTotal) / desktopFitCardDivisor,
          ),
        ),
      )
    : DESKTOP_DEFAULT_CARD_WIDTH;

  const fittedDesktopCardHeight = Math.round(
    (fittedDesktopCardWidth * DESKTOP_DEFAULT_CARD_HEIGHT) /
      DESKTOP_DEFAULT_CARD_WIDTH,
  );
  const desktopPageStep =
    desktopFitCardDivisor * (fittedDesktopCardWidth + desktopCardGap);
  const desktopMaxScrollOffset = Math.max(
    0,
    desktopContentWidth - desktopContainerWidth,
  );
  // Scrollable when the rendered cards overflow the container, measured not counted.
  const isCarouselScrollable = desktopMaxScrollOffset > 1;
  const showDesktopChevrons = isDesktopMeasured && isCarouselScrollable;
  const canScrollDesktopLeft = desktopScrollOffset > 1;
  const canScrollDesktopRight =
    desktopScrollOffset < desktopMaxScrollOffset - 1;

  const scrollDesktopBy = (delta: number) => {
    const nextOffset = Math.max(
      0,
      Math.min(desktopMaxScrollOffset, desktopScrollOffset + delta),
    );
    listRef.current?.scrollToOffset({ offset: nextOffset, animated: true });
  };

  const handleDesktopPrev = () => scrollDesktopBy(-desktopPageStep);
  const handleDesktopNext = () => scrollDesktopBy(desktopPageStep);
  const desktopChevronTop = Math.max(
    0,
    Math.round(fittedDesktopCardHeight / 2 - DESKTOP_CHEVRON_SIZE / 2),
  );

  const contentCardProps: ContentCardViewProps[] = section.articles.map(
    (article, index) => ({
      image: {
        url: getArticleViewportImageSrc({
          backgroundImage: article.backgroundImage,
          fallbackSrc: article.backgroundImageUrl,
          fallbackWidth: fallbackViewportWidth,
        }),
      },
      contentDetailsPropsValues: {
        orientation: ContentCardOrientation.VERTICAL,
        width: fittedDesktopCardWidth,
        imageWidth: fittedDesktopCardWidth,
        primaryText: article.title,
        secondaryText: article.subTitle,
      },
      imageHeight: fittedDesktopCardHeight,
      imageWidth: fittedDesktopCardWidth,
      onPress: () => {
        handleArticlePress(index, article);
      },
    }),
  );

  return (
    <View style={[styles.section, !isLast && styles.sectionSpacing]}>
      <Text
        style={[
          getTextStyle(
            pd.sys.typescale.body_text_medium,
            pd.sys.colour.content_primary,
          ),
          styles.title,
        ]}
      >
        {section.title}
      </Text>
      {isMobileWidth ? (
        <View style={styles.mobileCarouselInset}>
          <ContentCardGroupView
            orientation={
              new Orientation.Horizontal(1, 1, pd.ref.space.spacing20.px, 0)
            }
            contentCardProps={contentCardProps}
            flatListProps={
              {
                ref: listRef,
                onScroll: handleScroll,
                onViewableItemsChanged,
                scrollEventThrottle: 16,
                showsHorizontalScrollIndicator: false,
              } as unknown as FlatListProps<ContentCardViewProps | number>
            }
          />
        </View>
      ) : (
        <View style={styles.desktopCarouselWrapper}>
          <View
            style={styles.desktopCarouselInset}
            onLayout={onDesktopInsetLayout}
          >
            <FlatList
              ref={listRef}
              testID="DesktopArticleCarouselFlatList"
              data={contentCardProps}
              horizontal
              nestedScrollEnabled
              style={styles.desktopCarouselList}
              renderItem={({ item, index }) => (
                <HoverableArticleCard
                  card={item}
                  index={index}
                  total={contentCardProps.length}
                  cardWidth={
                    isDesktopMeasured
                      ? fittedDesktopCardWidth
                      : DESKTOP_DEFAULT_CARD_WIDTH
                  }
                />
              )}
              keyExtractor={(item, index) =>
                `${item.contentDetailsPropsValues?.primaryText ?? 'article'}-${index}`
              }
              onScroll={handleScroll}
              onContentSizeChange={(width: number) =>
                setDesktopContentWidth(width)
              }
              onViewableItemsChanged={onViewableItemsChanged}
              scrollEventThrottle={16}
              showsHorizontalScrollIndicator={false}
            />
          </View>

          {showDesktopChevrons && canScrollDesktopLeft && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Scroll articles left"
              style={[
                styles.desktopChevron,
                styles.desktopChevronLeft,
                { top: desktopChevronTop },
              ]}
              onPress={handleDesktopPrev}
            >
              <IconView
                icon={pd.icon.main.navigation.chevron_left}
                height={pd.ref.space.spacing50.px}
                width={pd.ref.space.spacing50.px}
                tint={getColor(pd.sys.colour.content_secondary)}
              />
            </Pressable>
          )}

          {showDesktopChevrons && canScrollDesktopRight && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Scroll articles right"
              style={[
                styles.desktopChevron,
                styles.desktopChevronRight,
                { top: desktopChevronTop },
              ]}
              onPress={handleDesktopNext}
            >
              <IconView
                icon={pd.icon.main.navigation.chevron_right}
                height={pd.ref.space.spacing50.px}
                width={pd.ref.space.spacing50.px}
                tint={getColor(pd.sys.colour.content_secondary)}
              />
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
}

function ArticleCarouselView({
  articleCarouselData,
}: {
  articleCarouselData: ArticleCarouselData;
}) {
  const { isMobileWidth } = useDeviceInfo();

  if (!articleCarouselData || !articleCarouselData.sections.length) {
    return null;
  }

  const visibleSections = articleCarouselData.sections.filter(
    (section) => section.articles.length > 0,
  );

  if (!visibleSections.length) {
    return null;
  }

  const desktopFitCardDivisor = Math.min(
    DESKTOP_FIT_MAX_CARDS,
    Math.max(...visibleSections.map((s) => s.articles.length)),
  );

  return (
    <View style={styles.container}>
      {visibleSections.map((section, index) => (
        <ArticleCarouselSection
          key={`${section.title}-${index}`}
          section={section}
          isMobileWidth={isMobileWidth}
          isLast={index === visibleSections.length - 1}
          desktopFitCardDivisor={desktopFitCardDivisor}
        />
      ))}
    </View>
  );
}

export default ArticleCarouselView;
